import {StyleSheet} from "aphrodite";
import {useCallback, useEffect, useId, useState} from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import type {MediaSize} from "@khanacademy/wonder-blocks-layout";
import {semanticColor} from "@khanacademy/wonder-blocks-tokens";
import {publishAnalyticsEventForMinorConversion} from "@khan/analytics-learning-time";
import {usePrefersReducedMotion} from "@khan/data-prefers-reduced-motion";
import {setCoeditorPaused, useCoeditorContext} from "@khan/guide-coeditor-core";
import {useGuideState} from "@khan/guide-state";
import {a11yStyles} from "@khan/shared-styles";
import {useLocation} from "@khan/urls";

import Coeditor from "./coeditor.tsx";
import Expander from "./expander.tsx";

// We redefine these here because importing them causes a Chromatic failure in CI.
const CONTENT_SIDEBAR_WIDTH = 485;
const KHANMIGO_SIDEBAR_MIN_WIDTH = 400;

export default function CoeditorDrawer(props: {
    mediaSize: MediaSize;
}): React.ReactElement | null {
    const {mediaSize} = props;

    const {open: isCoeditorOpen} = useCoeditorContext();
    const navigation = useGuideState("navigation");
    const threadId = useGuideState("threadId");
    const {href} = useLocation();
    const prefersReducedMotion = usePrefersReducedMotion();
    const [isCollapsed, setIsCollapsed] = useState(false);

    useEffect(() => {
        if (isCoeditorOpen) {
            setIsCollapsed(false);

            // If the editor is opening after being closed, record an event
            publishAnalyticsEventForMinorConversion({
                eventType: "AiGuideCoeditorOpened",
                eventSchemaVersion: 2,
                aiGuideThreadId: threadId ?? "",
                aiGuideNavigationId: navigation.id,
                currentPageURL: href,
            });
        }
    }, [isCoeditorOpen, href, threadId, navigation.id]);

    const toggleCollapsed = useCallback(
        () =>
            setIsCollapsed((collapsed) => {
                const newValue = !collapsed;
                setCoeditorPaused(newValue);
                return newValue;
            }),
        [],
    );

    const editorId = useId();
    const renderCollapsed = isCollapsed && mediaSize !== "small";

    if (!isCoeditorOpen) {
        return null;
    }

    return (
        <View
            style={[
                styles.container,
                renderCollapsed && styles.collapsed,
                mediaSize === "small" && styles.smallScreen,
                prefersReducedMotion && a11yStyles.prefersReducedMotion,
            ]}
        >
            {mediaSize !== "small" && (
                <View style={styles.expander}>
                    <Expander
                        expanded={!isCollapsed}
                        onClick={toggleCollapsed}
                        editorId={editorId}
                    />
                </View>
            )}
            <View
                style={[
                    styles.subcontainer,
                    renderCollapsed && styles.subcontainerCollapsed,
                ]}
                aria-hidden={!isCoeditorOpen ? "true" : undefined}
            >
                <Coeditor editorId={editorId} showDocumentActionButtons />
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        height: "100%",
        position: "relative",
        borderInlineEnd: `1px solid ${semanticColor.core.border.neutral.subtle}`,
        display: "flex",
        flexDirection: "row",
        backgroundColor: semanticColor.core.background.base.default,
    },
    smallScreen: {
        width: "100%",
    },
    collapsed: {
        border: "none",
        backgroundColor: semanticColor.core.background.base.subtle,
    },
    subcontainer: {
        height: "100%",
        width: `calc(100vw - ${CONTENT_SIDEBAR_WIDTH}px - ${KHANMIGO_SIDEBAR_MIN_WIDTH}px)`,
        overflow: "hidden",
    },
    subcontainerCollapsed: {
        width: 0,
    },
    expander: {
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },
});
